import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Languages, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import type { BotConfig, Template } from '../../config/types';
import { hebrewSettings, hebrewModelSelectionConflict, isHebrewTemplate, needsHebrewFallbackChange, selectedHebrewPatch } from '../../config/templateSettings';
import { useApiHeaders } from '../../hooks/useApiHeaders';
import { useHebrewSetupPreference } from '../hooks/useHebrewSetupPreference';
import './HebrewSetupDialog.css';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApply: (patch: Partial<BotConfig>) => void;
  config?: Partial<BotConfig>;
  initialPatch?: Partial<BotConfig>;
  template?: Template;
  botId?: string;
  source?: 'template' | 'country' | 'sidekick' | 'greeting' | 'language' | 'voice' | 'phone';
  preserveFields?: readonly (keyof BotConfig)[];
  confirmLabel?: string;
}

export default function HebrewSetupDialog({ open, onOpenChange, onApply, config, initialPatch, template, botId, source = 'template', preserveFields, confirmLabel = 'Apply selected settings' }: Props) {
  const apiHeaders = useApiHeaders('');
  const preference = useHebrewSetupPreference(botId);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [savingPreference, setSavingPreference] = useState(false);
  const [preferenceError, setPreferenceError] = useState('');
  const savingRef = useRef(false);
  const [loaded, setLoaded] = useState<Template>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [reviewedBot, setReviewedBot] = useState<{ id?: string } | null>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const reviewRequested = reviewedBot !== null && reviewedBot.id === botId;
  const showIntro = source === 'country' && !reviewRequested;
  // Snapshot only on opening; edits elsewhere must not reset the user's toggles.
  const configRef = useRef(config);
  configRef.current = config;
  const initialPatchRef = useRef(initialPatch);
  initialPatchRef.current = initialPatch;
  const activeTemplate = template || loaded;
  const settings = useMemo(() => activeTemplate ? hebrewSettings(activeTemplate) : [], [activeTemplate]);

  useEffect(() => {
    setReviewedBot(null);
    setDontShowAgain(false);
    setPreferenceError('');
  }, [open, source, botId]);

  useEffect(() => {
    if (reviewRequested) titleRef.current?.focus();
  }, [reviewRequested]);

  useEffect(() => {
    if (!open || showIntro) return;
    if (template) { setError(''); setLoading(false); return; }
    const controller = new AbortController();
    setLoading(true);
    setError('');
    setLoaded(undefined);
    const scope = botId ? `?bot_id=${encodeURIComponent(botId)}` : '';
    fetch(`/api/customer/templates${scope}`, { headers: apiHeaders(), signal: controller.signal })
      .then(async response => {
        if (!response.ok) throw new Error('The Hebrew template could not be loaded. Please try again.');
        const data: { templates?: Template[] } = await response.json();
        const found = data.templates?.find(isHebrewTemplate);
        if (!found) throw new Error('The Hebrew template is not available yet. Please try again later.');
        if (!controller.signal.aborted) setLoaded(found);
      })
      .catch(err => { if (!controller.signal.aborted) setError(err.message || 'Could not load the Hebrew template.'); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [open, showIntro, template, botId, apiHeaders, retry]);

  useEffect(() => {
    if (!open || showIntro) return;
    setSelected(new Set(settings.filter(setting => {
      if (initialPatchRef.current) return setting.fields.some(field => initialPatchRef.current?.[field] !== undefined);
      if (source === 'voice' && setting.id === 'emotions_yaml') return false;
      // An empty country is how Custom language selection stays in custom mode.
      if (source === 'language' && setting.id === 'default_country') return false;
      if (preserveFields?.some(field => setting.fields.includes(field) && configRef.current?.[field] !== undefined)) return false;
      if (setting.id === 'llm_model_priority_2') return !!activeTemplate && needsHebrewFallbackChange(activeTemplate, configRef.current);
      const keepsContent = source !== 'template' && (setting.id === 'prompt_base' || setting.id === 'welcome_message');
      return !(keepsContent && configRef.current?.[setting.id]);
    }).map(setting => setting.id)));
  }, [open, showIntro, settings, source, activeTemplate, preserveFields]);

  const close = async () => {
    if (savingRef.current) return;
    if (!dontShowAgain || !botId) { onOpenChange(false); return; }
    savingRef.current = true;
    setSavingPreference(true);
    setPreferenceError('');
    try {
      await preference.dismiss();
      onOpenChange(false);
    } catch {
      setPreferenceError('Could not save “Don’t show again”. Please try again.');
    } finally {
      savingRef.current = false;
      setSavingPreference(false);
    }
  };

  const apply = async () => {
    if (!activeTemplate || savingRef.current) return;
    const patch = selectedHebrewPatch(activeTemplate, selected, config);
    savingRef.current = true;
    setSavingPreference(true);
    setPreferenceError('');
    try {
      if (botId) await preference.markApplied();
      onApply(patch);
      onOpenChange(false);
    } catch {
      setPreferenceError('Could not save your template choice. Please try again.');
    } finally {
      savingRef.current = false;
      setSavingPreference(false);
    }
  };

  const groups = Array.from(new Set(settings.map(setting => setting.group)));
  const toggle = (id: string, checked: boolean) => setSelected(previous => {
    const next = new Set(previous);
    if (checked) next.add(id); else next.delete(id);
    // Keeping the current primary should also keep its fallback by default.
    // The fallback remains available as an independent choice below.
    if (id === 'llm_model' && !checked) next.delete('llm_model_priority_2');
    return next;
  });
  const modelConflict = !!activeTemplate && hebrewModelSelectionConflict(activeTemplate, selected, config);

  return <Dialog open={open} onOpenChange={next => { if (!next) void close(); }}>
    <DialogContent className="hebrew-setup" style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', maxWidth: showIntro ? 520 : 680 }}>
      <header className="hebrew-setup-header">
        <div className="hebrew-setup-eyebrow"><Languages size={17} /> BUILT FOR HEBREW</div>
        <Button variant="ghost" size="icon" className="hebrew-setup-close" aria-label="Close Hebrew setup" disabled={savingPreference} onClick={() => void close()}><X /></Button>
        <DialogTitle ref={titleRef} tabIndex={-1} className="hebrew-setup-title">{showIntro ? 'Start with our Hebrew template.' : 'Give your bot a natural Hebrew voice.'}</DialogTitle>
        <DialogDescription className="hebrew-setup-description">
          {showIntro
            ? 'For calls in Hebrew, we recommend our Hebrew template. It includes Tal Katz, our 3.4 voice model, and settings tuned for natural conversation.'
            : <>{source === 'sidekick' ? 'Sidekick noticed your bot is for a Hebrew-speaking or Israeli audience. ' : ''}Start with Tal Katz and our 3.4 speech model. Choose the settings you want to apply.</>}
        </DialogDescription>
      </header>
      {!showIntro && <div className="hebrew-setup-body" aria-busy={loading}>
        {loading && <p role="status">Loading recommended settings…</p>}
        {error && <div role="alert" className="hebrew-setup-error"><p>{error}</p><Button variant="outline" className="btn" onClick={() => setRetry(value => value + 1)}>Try again</Button></div>}
        {!loading && !error && settings.length > 0 && <>
          <div className="hebrew-setup-selection">
            <span>{selected.size} of {settings.length} settings selected</span>
            <Button variant="ghost" className="hebrew-setup-select-all" disabled={savingPreference} onClick={() => setSelected(selected.size === settings.length ? new Set() : new Set(settings.map(setting => setting.id)))}>
              {selected.size === settings.length ? 'Deselect all' : 'Select all'}
            </Button>
          </div>
          {groups.map(group => <section key={group} className="hebrew-setup-group" aria-label={group}>
            <h3>{group}</h3>
            <div className="hebrew-setup-rows">
              {settings.filter(setting => setting.group === group).map(setting => <div key={setting.id} className="hebrew-setup-row">
                <Label htmlFor={`hebrew-${setting.id}`} className="hebrew-setup-setting">
                  <span className="hebrew-setup-label">{setting.label}</span>
                  <span className="hebrew-setup-value" dir="auto">{setting.value}</span>
                  {setting.id === 'llm_model_priority_2' && <span className="hebrew-setup-note">Primary and fallback models must differ. Your current fallback is kept unless this is selected.</span>}
                  {(setting.id === 'prompt_base' || setting.id === 'welcome_message') && config?.[setting.id] && <span className="hebrew-setup-note">Replaces your current {setting.id === 'prompt_base' ? 'system prompt' : 'greeting'} if selected.</span>}
                </Label>
                <Switch id={`hebrew-${setting.id}`} aria-label={setting.label} disabled={savingPreference} checked={selected.has(setting.id)} onCheckedChange={checked => toggle(setting.id, checked)} />
                {setting.preview && <details className="hebrew-setup-preview"><summary>Preview system prompt</summary><p className="hebrew-setup-note">Replace the [[placeholders]] with your business details and connect any tools named in the prompt before going live.</p><pre dir="rtl" lang="he">{setting.preview}</pre></details>}
              </div>)}
            </div>
          </section>)}
        </>}
      </div>}
      <footer className="hebrew-setup-footer">
        {preferenceError && <p role="alert" className="hebrew-setup-error">{preferenceError}</p>}
        {!showIntro && modelConflict && <p role="alert" className="hebrew-setup-error">These choices would use the same conversation model for primary and fallback. Select both recommended models, or keep both current models.</p>}
        {botId && <Label className="hebrew-setup-dismiss">
          <Checkbox checked={dontShowAgain} disabled={savingPreference} onCheckedChange={value => setDontShowAgain(value === true)} />
          <span>Don’t show again for this bot</span>
        </Label>}
        {showIntro ? null : <span>{source === 'template' ? 'Customize business details after applying.' : 'Changes stay in your draft until you publish.'}</span>}
        <div>
          <Button variant="outline" className="btn" disabled={savingPreference} onClick={() => void close()}>Not now</Button>
          {showIntro ? <Button className="btn brand" disabled={savingPreference} onClick={() => setReviewedBot({ id: botId })}>Review settings</Button> : <Button className="btn brand" disabled={savingPreference || loading || !!error || !activeTemplate || selected.size === 0 || modelConflict} onClick={() => void apply()}>{savingPreference ? 'Saving…' : confirmLabel}</Button>}
        </div>
      </footer>
    </DialogContent>
  </Dialog>;
}
